import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "A stock chart shows a company's share price over time. The basics are: the X-axis is time, the Y-axis is price, the line (or candle) shows where price went, and the bars at the bottom show trading volume. Time frames matter — a one-day chart and a five-year chart of the same stock can look like opposite stories. Start with the longest time frame and work inward.";

const FAQS = [
  { q: 'What\'s the difference between a line chart and a candlestick chart?', a: 'A line chart shows only the closing price each day. Candlesticks show open, high, low, and close. Candlesticks pack more information but take more practice to read.' },
  { q: 'What does volume tell you?', a: 'Volume is the number of shares traded. Big price moves on high volume tend to be more meaningful than the same move on low volume. A breakout on no volume is suspect.' },
  { q: 'What time frame should I use?', a: 'It depends on your horizon. Long-term investors care about 5–10 year charts. Swing traders use daily and weekly. Day traders zoom into 1-minute candles. Always check more than one.' },
  { q: 'Are technical indicators (RSI, MACD) worth learning?', a: 'For long-term investing, mostly no. For short-term trading, some traders find value, but the evidence is mixed. Master the basics before reaching for indicators.' },
  { q: 'Why does a stock split distort old charts?', a: 'When a company splits its stock (say 4-for-1), the share price drops to a quarter overnight. Most modern charts adjust historical prices for splits automatically; older paper charts didn\'t.' },
];

export default function HowToReadAStockChartPage() {
  return (
    <BlogArticleLayout
      slug="how-to-read-a-stock-chart"
      title="How to Read a Stock Chart (With a Worked Example)"
      description="A stock chart looks intimidating but really only has a few moving parts. Learn the price line, volume, time frames, and what to ignore."
      keywords="how to read a stock chart, stock chart for beginners, candlestick chart, stock chart basics, reading stock charts, stock chart explained"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['how-to-value-a-stock', 'stock-picking', 'stock-wordle']}
    >
      <section>
        <h2>What does a stock chart actually show?</h2>
        <p>
          A stock chart is a visual record of a company's share price over time. The basic anatomy:
        </p>
        <ul>
          <li><strong>X-axis</strong> — time, going left (older) to right (newer).</li>
          <li><strong>Y-axis</strong> — price per share.</li>
          <li><strong>Price line or candles</strong> — the actual price history.</li>
          <li><strong>Volume bars</strong> — usually at the bottom, showing how many shares traded each day.</li>
        </ul>
        <p>
          Everything else (moving averages, indicators, drawings) is optional. Master the four basics first.
        </p>
      </section>

      <section>
        <h2>Line chart vs candlestick chart: which to use?</h2>
        <p>
          Two main chart styles:
        </p>
        <ul>
          <li><strong>Line chart</strong> — connects closing prices. Clean, easy to read trends. Best for long horizons.</li>
          <li><strong>Candlestick chart</strong> — each candle shows open, close, high, and low. Green if price closed above open, red if below. Best for active trading.</li>
        </ul>
        <p>
          For most long-term investors, line charts are enough. Switch to candles only when you're trying to read short-term momentum.
        </p>
      </section>

      <section>
        <h2>What time frame should you look at?</h2>
        <p>
          Always look at multiple time frames. The same stock can look like a great buy on a 5-year chart and a falling knife on a 1-month chart.
        </p>
        <ul>
          <li><strong>10-year</strong> — answers "is this a long-term winner?"</li>
          <li><strong>1-year</strong> — answers "what's the recent trend?"</li>
          <li><strong>3-month</strong> — answers "is something happening right now?"</li>
        </ul>
        <p>
          Worked example: NVIDIA on a 10-year chart looks like a vertical line up. On a 6-month chart it might show a 20% pullback. Both are true. Both matter.
        </p>
      </section>

      <section>
        <h2>What about volume?</h2>
        <p>
          Volume confirms or undermines what price is doing. A stock that breaks out to a new high on huge volume is a stronger signal than the same breakout on quiet volume.
          Volume spikes also flag earnings days, big news, or institutional repositioning. If you see a sudden volume spike, search for news from that day to understand what triggered it.
        </p>
      </section>

      <section>
        <h2>How does chart reading fit into stock selection?</h2>
        <p>
          For long-term investors, the chart is a context check, not a buy signal. The order of operations:
        </p>
        <ul>
          <li><strong>1. Understand the business.</strong> What does it sell, to whom, with what margins?</li>
          <li><strong>2. Check the fundamentals.</strong> Use ratios like <Link to="/blog/what-is-pe-ratio">P/E</Link>, <Link to="/blog/what-is-roe">ROE</Link>, and <Link to="/blog/what-is-free-cash-flow">free cash flow</Link>.</li>
          <li><strong>3. Look at the chart.</strong> Long-term direction, recent action, volume context.</li>
          <li><strong>4. Decide.</strong> Buy, watchlist, or pass.</li>
        </ul>
        <p>
          Want to practise reading charts daily? <Link to="/">Wallstreetle</Link> shows you a real anonymised chart every day and asks you to identify the company from it.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
